import { formatMin, type Span } from '@wiztime/core';
import { cx } from '../../../lib/cx';
import { formatRange } from '../../../lib/format';
import { percentOf } from './laneModel';
import styles from './Timeline.module.css';

export const GhostVariant = {
  Drag: 'drag',
  Pending: 'pending',
  Hover: 'hover',
  /** Dashed outline at the original place of a block being moved (ui-redesign §3.4.4). */
  Origin: 'origin',
  /** A tile dropped here would write nothing (lane loading, overlay open, write in flight). */
  Blocked: 'blocked',
} as const;
export type GhostVariant = (typeof GhostVariant)[keyof typeof GhostVariant];

interface DragGhostProps {
  readonly span: Span;
  readonly dayStartMin: number;
  readonly variant: GhostVariant;
  readonly label?: string;
  /** Hover ghost of the day lane (§3.4.1): first line is the time range of the click. */
  readonly showRange?: boolean;
  /** Quick-mark mode suffix after the range (2026-10b §4.4): "（一次）" or "（连续）". */
  readonly suffix?: string;
  /** Learning-period second line ("click to record · drag to select"), until it retires. */
  readonly hint?: string;
  /** Touch long press armed this preview (§3.3): stronger outline, short scale pulse. */
  readonly armed?: boolean;
}

/**
 * A block-shaped outline: drag preview, pending block awaiting a project, hover hint or the
 * original place of a moved block. The time readout of a drag lives in the floating tip
 * (FloatTip), never inside the lane; the hover ghost may carry its own two lines (§3.4.1).
 */
export function DragGhost({
  span,
  dayStartMin,
  variant,
  label,
  showRange,
  suffix,
  hint,
  armed,
}: DragGhostProps) {
  const top = percentOf(span.startMin, dayStartMin);
  const height = percentOf(span.endMin, dayStartMin) - top;
  return (
    <div
      className={cx(styles.ghost, styles[`ghost-${variant}`], armed === true && styles.ghostArmed)}
      style={{ top: `calc(${top}% + 1px)`, height: `max(6px, calc(${height}% - 2px))` }}
      data-testid={`ghost-${variant}`}
      data-start={formatMin(span.startMin)}
      data-end={formatMin(span.endMin)}
    >
      {label !== undefined ? <span className={styles.ghostLabel}>{label}</span> : null}
      {showRange === true || hint !== undefined ? (
        <span className={styles.ghostText}>
          {showRange === true ? (
            <span className={styles.ghostRange}>
              {formatRange(span.startMin, span.endMin)}
              {suffix ?? ''}
            </span>
          ) : null}
          {hint !== undefined ? (
            <span className={styles.ghostHint} data-testid="ghost-hint">
              {hint}
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}
